import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';

interface LeaderboardEntry {
  rank: number;
  user: { username: string };
  score: number;
  duration: number;
  errorRate: number;
  gameMode: string;
  difficulty: string;
}

export const Leaderboard: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [filters, setFilters] = useState({
    gameMode: 'ALL',
    difficulty: 'ALL',
  });
  const [loading, setLoading] = useState(false);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await apiService.getLeaderboard({
        gameMode: filters.gameMode === 'ALL' ? undefined : filters.gameMode,
        difficulty: filters.difficulty === 'ALL' ? undefined : filters.difficulty,
        limit: 10,
      });
      setLeaderboard(data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, [filters]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-lg p-4"
    >
      <h3 className="text-xl font-bold mb-4">🏆 排行榜</h3>

      <div className="flex gap-2 mb-4">
        <select
          value={filters.gameMode}
          onChange={(e) => setFilters({ ...filters, gameMode: e.target.value })}
          className="px-3 py-1 border rounded text-sm"
        >
          <option value="ALL">所有模式</option>
          <option value="FREE">自由模式</option>
          <option value="TIMED">限时挑战</option>
          <option value="QUIZ">知识问答</option>
        </select>
        <select
          value={filters.difficulty}
          onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
          className="px-3 py-1 border rounded text-sm"
        >
          <option value="ALL">所有难度</option>
          <option value="EASY">简单</option>
          <option value="MEDIUM">中等</option>
          <option value="HARD">困难</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-4">加载中...</div>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((entry) => (
            <motion.div
              key={`${entry.rank}-${entry.user.username}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                  entry.rank === 1 ? 'bg-yellow-500' :
                  entry.rank === 2 ? 'bg-gray-400' :
                  entry.rank === 3 ? 'bg-orange-600' : 'bg-blue-500'
                }`}>
                  {entry.rank}
                </div>
                <div>
                  <div className="font-medium">{entry.user.username}</div>
                  <div className="text-xs text-gray-500">
                    {entry.gameMode} · {entry.difficulty}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold">{entry.score}分</div>
                <div className="text-xs text-gray-500">
                  {Math.round(entry.duration / 60)}min · {Math.round(entry.errorRate * 100)}%错误
                </div>
              </div>
            </motion.div>
          ))}
          {leaderboard.length === 0 && (
            <div className="text-center py-4 text-gray-500">暂无排行数据</div>
          )}
        </div>
      )}
    </motion.div>
  );
};
